import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, BadgeCheck } from "lucide-react";
import { VideoModal } from "./VideoModal";
import { HeroNetworkCanvas } from "./HeroNetworkCanvas";

const HERO_BG =
  "https://images.unsplash.com/photo-1617728035903-61776037e689?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzc4NDczMTk1fDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 + i * 0.07, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <section
      id="top"
      data-testid="hero-section"
      data-nav-dark
      className="relative isolate min-h-[100svh] bg-brand-950 text-white overflow-hidden grain-overlay"
    >
      {/* Solid dark base */}
      <div aria-hidden className="absolute inset-0 bg-brand-950" />
      {/* Background image (subtle) */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center opacity-[0.16] mix-blend-luminosity"
        style={{ backgroundImage: `url('${HERO_BG}')` }}
      />
      {/* Darkening overlays */}
      <div aria-hidden className="absolute inset-0 bg-brand-950/80" />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-brand-950 via-brand-950/85 to-brand-950" />
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(200,75,49,0.22),transparent_55%)]" />
      <div aria-hidden className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(10,25,47,0.6),transparent_60%)]" />

      {/* Faint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-32 md:pb-40">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.06 } } }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center"
        >
          {/* LEFT: Content */}
          <div className="lg:col-span-7">
            <motion.h1
              variants={fadeUp}
              custom={2}
              data-testid="hero-title"
              className="font-display font-medium text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight"
            >
              Digital Insurance
              <br />
              Delivered <span className="text-copper">at Scale</span>
              <span className="text-copper">.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={3}
              className="mt-8 max-w-xl text-lg md:text-xl text-slate-300/90 leading-relaxed font-light"
              data-testid="hero-subtitle"
            >
              AI-powered, modular, API-first platform for{" "}
              <span className="text-white">Life and General Insurance</span>.
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={4}
              className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-slate-300"
            >
              <span className="text-white font-medium">Launch faster.</span>
              <span className="h-1 w-1 bg-copper rounded-full" />
              <span className="text-white font-medium">Operate efficiently.</span>
              <span className="h-1 w-1 bg-copper rounded-full" />
              <span className="text-white font-medium">Scale intelligently.</span>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={5}
              className="mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4"
            >
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-copper hover:bg-copper-600 text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                Request a demo
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center justify-center gap-3 border-2 border-copper text-copper hover:bg-copper hover:text-white px-8 py-4 text-sm font-medium tracking-[0.12em] uppercase transition-colors"
              >
                <PlayCircle className="h-4 w-4" />
                Explore platform
              </button>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={6}
              className="mt-12 inline-flex items-center gap-4 border border-white/15 bg-white/[0.03] backdrop-blur-sm pl-5 pr-7 py-4 max-w-2xl"
              data-testid="hero-trust-badge"
            >
              <BadgeCheck className="h-5 w-5 md:h-6 md:w-6 text-copper shrink-0" strokeWidth={2} />
              <span className="font-display text-base md:text-lg text-white tracking-tight leading-snug">
                <span className="text-copper">15+ insurers</span> trust Gradatim to run high-volume, mission-critical operations.
              </span>
            </motion.div>
          </div>

          {/* RIGHT: Visual */}
          <motion.div
            variants={fadeUp}
            custom={2}
            className="lg:col-span-5 hidden lg:block"
          >
            <HeroVisual />
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-px bg-white/10" />

      <VideoModal
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        src="/platform-video.mp4"
        title="Gradatim Platform — Walkthrough"
      />
    </section>
  );
};

/**
 * Hero visual — animated network canvas (interconnected nodes,
 * flowing data packets, expanding rings, breathing teal/blue palette).
 */
const HeroVisual = () => {
  return (
    <div className="relative aspect-video w-full max-w-[560px] mx-auto" data-testid="hero-visual">
      <div aria-hidden className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(56,232,232,0.18),transparent_60%)]" />
      <div className="absolute inset-0 border border-white/10" />
      <div className="absolute inset-3 border border-white/5" />

      <div className="absolute inset-3 overflow-hidden">
        <HeroNetworkCanvas />
        {/* Soft vignette to blend with hero edges */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/5"
          style={{ boxShadow: "inset 0 0 80px rgba(2,6,23,0.55)" }}
        />
      </div>
    </div>
  );
};
